import { Injectable } from '@angular/core';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import moment from 'moment';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MembershipPaymentsAPIResponse, SubscriptionAPIResponse } from '../../types/responses';
import { CoreModule } from '../core.module';
import { ServerConnectionService } from './server-connection.service';

@Injectable({
	providedIn: CoreModule,
})
export class StripeService {
	public cards: Array<any> = [
		{ amex: 'Amex', discover: 'Discover', mastercard: 'Mastercard', visa: 'Visa', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay' },
	];

	private stripe: Stripe | null = null;

	constructor(private scs: ServerConnectionService) { }

	public async getInstance() {
		if (!this.stripe) {
			const stripeKey = environment.stripePK;
			this.stripe = await loadStripe(stripeKey);
		}
		return this.stripe;
	}

	// CUSTOMERS

	public getCustomer(customerId) {
		return this.scs.get$(`/api/users/customers/${customerId}`);
	}

	public updateCustomer(customerId, paymentMethodId, shouldAttach, setDefault) {
		const body = { paymentMethodId, shouldAttach, setDefault };
		return this.scs.http$('PUT', `/api/users/customers/${customerId}`, null, body);
	}

	public updateCustomerInfo(customerId, customer) {
		const body = customer;
		return this.scs.http$('PUT', `/api/stripe/customers/${customerId}`, null, body);
	}

	// PAYMENT METHODS

	public getCards(customerId) {
		return this.scs.http$('GET', `/api/users/cards/${customerId}`, null, null);
	}

	public defaultCustomerPaymentMethod(customerId, paymentMethodId) {
		const body = { paymentMethodId };
		return this.scs.http$('POST', `/api/users/cards/default/${customerId}`, null, body);
	}

	public removeCard(paymentMethodId) {
		return this.scs.http$('DELETE', `/api/users/cards/${paymentMethodId}`, null, null);
	}

	// SUBSCRIPTIONS

	public createSubscription(customer, plan, createCustomer, stripeCoupon = null) {
		const body = { customer, plan, createCustomer, stripeCoupon };
		return this.scs.http$('POST', '/api/subscriptions/', null, body);
	}

	public upgradeSubscription(customer, plan, paymentMethodId) {
		const body = { customer, plan, paymentMethodId };
		return this.scs.http$('POST', '/api/subscriptions/upgrade', null, body);
	}

	public retrySubscriptionPayment(subscriptionId: string) {
		return this.getSubscriptionsById(subscriptionId)
			.pipe(
				switchMap((result: SubscriptionAPIResponse) => {
					if (result.success && this.isSubscriptionWithDeclinedPayment(result.subscription)) {
						return this.scs.http$(
							'POST',
							'/api/subscriptions/retry-payment',
							null,
							{ stripeSubscriptionId: result.subscription.stripeSubscriptionId },
						);
					}
					return of(null);
				}),
			);
	}

	public cancelSubscription(customer) {
		const body = { customer };
		return this.scs.http$('POST', '/api/subscriptions/cancel', null, body);
	}

	public getSubscriptions(username) {
		return this.scs.http$('GET', `/api/subscriptions/${username}`, null, null);
	}

	public getSubscriptionsById(id) {
		return this.scs.http$('GET', `/api/subscriptions/getById/${id}`, null, null);
	}

	// Stripe customer
	public createStripeCustomer(email) {
		const body = {
			receipt_email: email,
		};

		return this.scs.http$('POST', '/api/users/create-stripe-customer', null, body);
	}

	public getCustomerPaymentMethods(stripeCustomerId) {
		const body = {
			stripeCustomerId,
		};

		return this.scs.http$('POST', '/api/stripe/customers/paymentMethods', null, body);
	}

	public getCustomerMembershipPayments() {
		return this.scs.get$<MembershipPaymentsAPIResponse>('/api/stripe/customers/membership-payments');
	}

	private isSubscriptionWithDeclinedPayment(subscription) {
		const DATE_TOLERANCE = 15;
		const isRenewalDateInTolerance = moment().isBefore(
			moment(subscription.renewalDate).add(DATE_TOLERANCE, 'days'),
		);

		return subscription.currentStatus === 'expired'
			&& subscription.paymentStatus === 'declined'
			&& isRenewalDateInTolerance;
	}
}
